import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import {
  Eye, EyeOff, Loader, AlertCircle, FileText,
  ExternalLink, Lock, LogOut, Building2,
} from "lucide-react";
import PortalProyectos from "./PortalProyectos";
import { mensajeError } from "../lib/comentarios";
import {
  aceptarInvitacion, perfilPortal, mensajeAceptacion,
  guardarTokenPendiente, leerTokenPendiente, borrarTokenPendiente,
} from "../lib/accesos";

// ── Parámetros de entrada (se leen al cargar el módulo, antes de que
// supabase-js limpie el fragmento #access_token… de la URL) ──────────────────
//   /portal?proyecto=PRY-…        abre el detalle (enlaces de notificación)
//   /portal?invitacion=<token>    invitación pendiente de aceptar
//   #…&type=invite|recovery       viene de "activar acceso" o "recuperar
//                                 contraseña": se pide definir contraseña
const QS            = new URLSearchParams(window.location.search);
const PROYECTO_LINK = QS.get("proyecto");
const HASH_TIPO     = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("type");
if (QS.get("invitacion")) guardarTokenPendiente(QS.get("invitacion"));
if (QS.get("invitacion")) {
  // No dejar el token en el historial ni en la barra de direcciones
  // (se conserva el fragmento #… para que supabase-js lea la sesión).
  const limpia = new URLSearchParams(window.location.search);
  limpia.delete("invitacion");
  const q = limpia.toString();
  window.history.replaceState(null, "", window.location.pathname + (q ? "?" + q : "") + window.location.hash);
}
const PIDE_CONTRASENA = HASH_TIPO === "invite" || HASH_TIPO === "recovery";
// Enlace de Supabase Auth vencido o ya usado: llega como #error=access_denied&error_code=otp_expired…
// (el enlace dura lo que indique "Email OTP expiration", 24 h; la invitación dura más).
const HASH_PARAMS = new URLSearchParams(window.location.hash.replace(/^#/, ""));
export const ENLACE_FALLIDO = HASH_PARAMS.get("error_code") || (HASH_PARAMS.get("error") ? "error" : null);
export const MENSAJE_ENLACE_FALLIDO =
  "El enlace de acceso venció o ya fue usado. Si ya definiste tu contraseña, ingresa con ella. " +
  "Si no, escribe a contacto@tuaplicacion.cl y te enviamos uno nuevo; tu invitación sigue vigente hasta su fecha de vencimiento.";
if (ENLACE_FALLIDO) {
  // Quitar el detalle del error de la barra de direcciones.
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
}

// ── Paleta ────────────────────────────────────────────────────────────────────
const C = {
  bg:"#f8fafc", white:"#ffffff", border:"#e2e8f0",
  accent:"#29abe2", accentL:"#e8f5fc",
  text:"#1e293b", sub:"#64748b", light:"#94a3b8",
  ok:"#10b981", okL:"#d1fae5",
  err:"#ef4444", errL:"#fee2e2",
  warn:"#f59e0b", warnL:"#fef3c7",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtCLP  = (n) => `$${Number(n||0).toLocaleString("es-CL")}`;
const fmtDate = (d) => d
  ? new Date(d+"T12:00:00").toLocaleDateString("es-CL",{day:"2-digit",month:"short",year:"numeric"})
  : "—";

const COT_EST = {
  Borrador:  { color:C.light,   bg:"#f1f5f9"  },
  Pendiente: { color:C.warn,    bg:C.warnL    },
  Aprobada:  { color:C.ok,      bg:C.okL      },
  Rechazada: { color:C.err,     bg:C.errL     },
  Expirada:  { color:"#f97316", bg:"#ffedd5"  },
};

const ING_EST = {
  emitida: { label:"Pendiente de pago", color:C.warn, bg:C.warnL },
  pagada:  { label:"Pagada",            color:C.ok,   bg:C.okL   },
  anulada: { label:"Anulada",           color:C.err,  bg:C.errL  },
};

// ── Componente principal ──────────────────────────────────────────────────────
export default function PortalCliente() {
  const [screen,   setScreen]   = useState("login"); // "login" | "loading" | "contrasena" | "portal"
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [authErr,  setAuthErr]  = useState(ENLACE_FALLIDO ? MENSAJE_ENLACE_FALLIDO : null);
  const [resetMsg, setResetMsg] = useState(null);
  const [logging,  setLogging]  = useState(false);

  const [contacto,     setContacto]     = useState(null);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [ingresos,     setIngresos]     = useState([]);
  const [proyectos,    setProyectos]    = useState([]);
  const [proyCargando, setProyCargando] = useState(false);
  const [proyError,    setProyError]    = useState("");
  const [tab,          setTab]          = useState(PROYECTO_LINK ? "proyectos" : "cotizaciones");
  const [perfil,       setPerfil]       = useState(null);
  const [aviso,        setAviso]        = useState(null);      // resultado de la invitación
  const [proyectoAbrir,setProyectoAbrir]= useState(PROYECTO_LINK);
  const [hayInvitacion]                 = useState(() => !!leerTokenPendiente());

  // Retomar sesión activa al montar (o la que trae el enlace de activación)
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      if (PIDE_CONTRASENA) setScreen("contrasena");
      else loadPortal();
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((evento) => {
      if (evento === "PASSWORD_RECOVERY") setScreen("contrasena");
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Login ──
  async function handleLogin(e) {
    e.preventDefault();
    setAuthErr(null);
    setResetMsg(null);
    setLogging(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setAuthErr("Email o contraseña incorrectos");
      setLogging(false);
      return;
    }
    await loadPortal();
    setLogging(false);
  }

  // ── Recuperar contraseña ──
  async function handleReset() {
    if (!email) { setAuthErr("Ingresa tu email para recuperar la contraseña"); return; }
    setAuthErr(null);
    await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/portal` });
    setResetMsg("Revisa tu email para recuperar tu contraseña");
  }

  // ── Cargar datos del portal tras autenticar ──
  async function loadPortal() {
    setScreen("loading");

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setScreen("login"); return; }

    // 1) Invitación pendiente: se acepta con la sesión actual. La base exige
    //    que el correo confirmado de la sesión coincida con el invitado.
    const token = leerTokenPendiente();
    if (token) {
      try {
        const r = await aceptarInvitacion(token);
        setAviso(mensajeAceptacion(r));
        if (r?.proyecto_id) { setProyectoAbrir(r.proyecto_id); setTab("proyectos"); }
        // Si el correo no coincide se conserva el token para reintentar con la cuenta correcta.
        if (r?.resultado !== "correo_distinto" && r?.resultado !== "correo_no_confirmado") borrarTokenPendiente();
      } catch (e) {
        setAviso({ tipo: "error", texto: mensajeError(e) });
      }
    }

    // 2) Perfil: rol y secciones disponibles (lo decide la base).
    let p;
    try { p = await perfilPortal(); }
    catch (e) {
      setAuthErr(mensajeError(e));
      setScreen("login");
      return;
    }
    if (p?.es_admin) { window.location.href = "/"; return; }
    setPerfil(p);

    // 3) Cotizaciones y facturas: solo si la cuenta está vinculada a un contacto.
    let c = null;
    if (p?.tiene_contacto) {
      const { data } = await supabase.from("contactos").select("*").eq("user_id", user.id).maybeSingle();
      c = data;
    }
    setContacto(c);

    const [{ data: cots }, { data: ings }] = await Promise.all([
      c ? supabase.from("cotizaciones").select("*").eq("rut", c.rut).order("created_at", { ascending: false })
        : Promise.resolve({ data: [] }),
      c ? supabase.from("ingresos").select("*").eq("rut_cliente", c.rut).order("fecha_emision", { ascending: false })
        : Promise.resolve({ data: [] }),
      cargarProyectos(),
    ]);
    setCotizaciones(cots || []);
    setIngresos(ings || []);
    if (!c) setTab("proyectos");
    setScreen("portal");
  }

  // ── Definir contraseña (activación de invitación o recuperación) ──
  async function guardarContrasena(nueva) {
    const { error } = await supabase.auth.updateUser({ password: nueva });
    if (error) throw error;
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    await loadPortal();
  }

  // Proyectos: la base de datos decide cuáles corresponden a este usuario
  // (portal_mis_proyectos usa la identidad autenticada, no el RUT del navegador).
  async function cargarProyectos() {
    setProyCargando(true); setProyError("");
    const { data, error } = await supabase.rpc("portal_mis_proyectos");
    if (error) setProyError(mensajeError(error));
    else setProyectos(data || []);
    setProyCargando(false);
  }

  // ── Logout ──
  async function handleLogout() {
    await supabase.auth.signOut();
    borrarTokenPendiente();
    setPerfil(null);
    setAviso(null);
    setContacto(null);
    setCotizaciones([]);
    setIngresos([]);
    setProyectos([]);
    setScreen("login");
  }

  // ════ LOADING ════
  if (screen === "loading") return (
    <div style={{minHeight:"100vh", background:C.bg, display:"flex",
      alignItems:"center", justifyContent:"center",
      fontFamily:"'DM Sans',sans-serif"}}>
      <GlobalStyle/>
      <div style={{textAlign:"center", color:C.sub}}>
        <Loader size={28} color={C.accent} className="spinning"
          style={{margin:"0 auto 12px"}}/>
        <div style={{fontSize:14}}>Cargando portal...</div>
      </div>
    </div>
  );

  // ════ DEFINIR CONTRASEÑA ════
  if (screen === "contrasena") return (
    <DefinirContrasena esActivacion={HASH_TIPO === "invite" || hayInvitacion}
      onGuardar={guardarContrasena} onCancelar={handleLogout}/>
  );

  // ════ LOGIN ════
  if (screen === "login") return (
    <div style={{minHeight:"100vh", background:C.bg, display:"flex",
      alignItems:"center", justifyContent:"center", padding:24,
      fontFamily:"'DM Sans',sans-serif"}}>
      <GlobalStyle/>

      <div style={{width:"100%", maxWidth:400}}>

        {/* Logo */}
        <div style={{textAlign:"center", marginBottom:32}}>
          <div style={{width:52, height:52, borderRadius:14, background:C.accent,
            display:"flex", alignItems:"center", justifyContent:"center",
            margin:"0 auto 16px", boxShadow:`0 6px 20px ${C.accent}40`}}>
            <Building2 size={26} color="#fff"/>
          </div>
          <div style={{fontSize:22, fontWeight:800}}>
            <span style={{color:C.accent}}>tu</span>
            <span style={{color:C.text}}>aplicacion</span>
          </div>
          <div style={{fontSize:13, color:C.sub, marginTop:6, fontWeight:500}}>
            Portal de Clientes
          </div>
        </div>

        {/* Card */}
        <div style={{background:C.white, border:`1px solid ${C.border}`, borderRadius:14,
          padding:"28px 28px", boxShadow:"0 4px 24px #0000000f"}}>

          {authErr && (
            <div style={{display:"flex", alignItems:"center", gap:8, padding:"10px 14px",
              background:C.errL, border:`1px solid ${C.err}30`, borderRadius:8,
              color:C.err, fontSize:13, fontWeight:500, marginBottom:18}}>
              <AlertCircle size={14}/> {authErr}
            </div>
          )}
          {resetMsg && (
            <div style={{padding:"10px 14px", background:C.okL,
              border:`1px solid ${C.ok}30`, borderRadius:8,
              color:C.ok, fontSize:13, fontWeight:500, marginBottom:18}}>
              ✓ {resetMsg}
            </div>
          )}

          <form onSubmit={handleLogin}>
            {/* Email */}
            <div style={{marginBottom:14}}>
              <label style={{display:"block", fontSize:12, fontWeight:600,
                color:C.sub, marginBottom:6}}>
                Email
              </label>
              <input
                type="email" value={email} autoComplete="email"
                onChange={e => setEmail(e.target.value)}
                placeholder="tu@empresa.cl"
                style={{width:"100%", padding:"10px 12px", borderRadius:8,
                  border:`1px solid ${C.border}`, background:C.white,
                  fontSize:14, color:C.text, fontFamily:"'DM Sans',sans-serif",
                  outline:"none", boxSizing:"border-box"}}
                required
              />
            </div>

            {/* Contraseña */}
            <div style={{marginBottom:22}}>
              <label style={{display:"block", fontSize:12, fontWeight:600,
                color:C.sub, marginBottom:6}}>
                Contraseña
              </label>
              <div style={{position:"relative"}}>
                <input
                  type={showPw ? "text" : "password"} value={password}
                  autoComplete="current-password"
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  style={{width:"100%", padding:"10px 40px 10px 12px", borderRadius:8,
                    border:`1px solid ${C.border}`, background:C.white,
                    fontSize:14, color:C.text, fontFamily:"'DM Sans',sans-serif",
                    outline:"none", boxSizing:"border-box"}}
                  required
                />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  style={{position:"absolute", right:10, top:"50%",
                    transform:"translateY(-50%)", background:"none", border:"none",
                    cursor:"pointer", color:C.light, display:"flex",
                    alignItems:"center", padding:2}}>
                  {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>

            <button type="submit" disabled={logging}
              style={{width:"100%", padding:"11px", borderRadius:8, border:"none",
                background: logging ? C.light : C.accent, color:"#fff",
                fontSize:14, fontWeight:700,
                cursor: logging ? "not-allowed" : "pointer",
                fontFamily:"'DM Sans',sans-serif",
                display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                boxShadow: logging ? "none" : `0 4px 14px ${C.accent}40`,
                transition:"background .15s"}}>
              {logging
                ? <><Loader size={14} className="spinning"/>Ingresando...</>
                : "Ingresar"}
            </button>
          </form>

          <div style={{textAlign:"center", marginTop:16}}>
            <button onClick={handleReset}
              style={{background:"none", border:"none", color:C.accent,
                fontSize:12, cursor:"pointer", fontFamily:"'DM Sans',sans-serif",
                textDecoration:"underline", opacity:.85}}>
              ¿Olvidaste tu contraseña?
            </button>
          </div>
        </div>

        {/* Footer */}
        <div style={{textAlign:"center", marginTop:24, fontSize:12, color:C.light,
          display:"flex", alignItems:"center", justifyContent:"center", gap:6}}>
          <Lock size={11}/> Portal seguro · tuaplicacion.cl
        </div>
      </div>
    </div>
  );

  // ════ PORTAL ════
  const tabs = [
    ...(contacto ? [
      { id:"cotizaciones", label:"Cotizaciones", count:cotizaciones.length },
      { id:"facturas",     label:"Facturas",     count:ingresos.length     },
    ] : []),
    { id:"proyectos",    label:"Proyectos",    count:proyectos.length,
      nuevos: proyectos.reduce((s, p) => s + (Number(p.comentarios_nuevos) || 0), 0) },
  ];

  const pendiente = ingresos
    .filter(i => i.estado === "emitida")
    .reduce((s, i) => s + Number(i.monto_total||0), 0);
  const pagado = ingresos
    .filter(i => i.estado === "pagada")
    .reduce((s, i) => s + Number(i.monto_total||0), 0);

  return (
    <div style={{minHeight:"100vh", background:"#f0f6fb",
      fontFamily:"'DM Sans',sans-serif", color:C.text}}>
      <GlobalStyle/>

      {/* ── Header ── */}
      <div className="portal-header" style={{background:C.white, borderBottom:`1px solid ${C.border}`,
        padding:"0 32px", height:56,
        display:"flex", alignItems:"center", justifyContent:"space-between",
        boxShadow:"0 1px 4px #0000000a"}}>

        {/* Logo */}
        <div style={{display:"flex", alignItems:"center", gap:8}}>
          <div style={{width:28, height:28, borderRadius:7, background:C.accent,
            display:"flex", alignItems:"center", justifyContent:"center"}}>
            <Building2 size={14} color="#fff"/>
          </div>
          <span style={{fontWeight:800, fontSize:15, color:C.accent}}>tu</span>
          <span style={{fontWeight:800, fontSize:15, color:C.text, marginLeft:-6}}>aplicacion</span>
        </div>

        {/* Bienvenido */}
        <div className="portal-bienvenida" style={{fontSize:13, color:C.sub, fontWeight:500}}>
          Bienvenido,{" "}
          <span style={{fontWeight:700, color:C.text}}>{contacto?.razon_social || perfil?.nombre}</span>
        </div>

        {/* Logout */}
        <button onClick={handleLogout}
          style={{display:"flex", alignItems:"center", gap:6, padding:"7px 14px",
            border:`1px solid ${C.border}`, borderRadius:8, background:C.white,
            color:C.sub, fontSize:12, fontWeight:600, cursor:"pointer",
            fontFamily:"'DM Sans',sans-serif", transition:"background .12s"}}
          onMouseEnter={e => e.currentTarget.style.background = C.bg}
          onMouseLeave={e => e.currentTarget.style.background = C.white}>
          <LogOut size={13}/> Cerrar sesión
        </button>
      </div>

      <div className="portal-main" style={{maxWidth:960, margin:"0 auto", padding:"28px 20px"}}>

        {aviso && (
          <div role={aviso.tipo === "error" ? "alert" : "status"}
            style={{display:"flex", alignItems:"center", gap:8, padding:"11px 14px", marginBottom:16,
              borderRadius:10, fontSize:13, fontWeight:500,
              background: aviso.tipo === "error" ? C.errL : C.okL,
              color: aviso.tipo === "error" ? C.err : C.ok,
              border:`1px solid ${aviso.tipo === "error" ? C.err : C.ok}30`}}>
            <AlertCircle size={14}/> <span style={{flex:1}}>{aviso.texto}</span>
            <button type="button" onClick={() => setAviso(null)} aria-label="Cerrar aviso"
              style={{background:"none", border:"none", color:"inherit", cursor:"pointer", fontSize:16}}>×</button>
          </div>
        )}

        {/* ── Tabs ── */}
        <div className="portal-tabs" role="tablist" style={{display:"flex", gap:4, marginBottom:20,
          background:C.white, border:`1px solid ${C.border}`,
          borderRadius:10, padding:4, boxShadow:"0 1px 3px #0000000a"}}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              role="tab" aria-selected={tab===t.id}
              style={{
                flex:1, display:"flex", alignItems:"center", justifyContent:"center",
                gap:7, padding:"9px 12px", borderRadius:7, border:"none",
                cursor:"pointer", fontSize:13, fontWeight:600,
                fontFamily:"'DM Sans',sans-serif", transition:"all .15s",
                background: tab===t.id ? C.accent : "transparent",
                color:       tab===t.id ? "#fff"   : C.sub,
              }}>
              {t.label}
              <span style={{
                padding:"1px 7px", borderRadius:10, fontSize:11, fontWeight:700,
                background: tab===t.id ? "rgba(255,255,255,.25)" : C.bg,
                color:       tab===t.id ? "#fff" : C.sub,
              }}>
                {t.count}
              </span>
              {t.nuevos > 0 && (
                <span aria-label={`${t.nuevos} comentarios nuevos`} title="Comentarios nuevos"
                  style={{width:8, height:8, borderRadius:"50%",
                    background: tab===t.id ? "#fff" : C.accent}}/>
              )}
            </button>
          ))}
        </div>

        {/* ── Tab Cotizaciones ── */}
        {tab === "cotizaciones" && (
          cotizaciones.length === 0
            ? <EmptyCard msg="No hay cotizaciones disponibles."/>
            : (
              <div style={{background:C.white, border:`1px solid ${C.border}`,
                borderRadius:12, overflow:"hidden", boxShadow:"0 1px 3px #0000000a"}}>
                <table style={{width:"100%", borderCollapse:"collapse", fontSize:13}}>
                  <thead>
                    <tr style={{background:C.bg}}>
                      {["ID","Fecha","Estado","Total","Acción"].map(h => (
                        <th key={h} style={{padding:"11px 16px", textAlign:"left",
                          fontWeight:600, color:C.sub, fontSize:11,
                          letterSpacing:.5, textTransform:"uppercase",
                          borderBottom:`1px solid ${C.border}`}}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cotizaciones.map(cot => {
                      const items = cot.items || [];
                      const sub   = items.reduce((a, i) => a + i.horas * i.ph, 0);
                      const tot   = sub + Math.round(sub * 0.19);
                      const est   = COT_EST[cot.estado] || COT_EST.Borrador;
                      return (
                        <tr key={cot.id}
                          style={{borderBottom:`1px solid ${C.border}`}}
                          onMouseEnter={e => e.currentTarget.style.background = C.bg}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <td style={{padding:"12px 16px", fontWeight:600, color:C.text}}>
                            {cot.id}
                          </td>
                          <td style={{padding:"12px 16px", color:C.sub}}>
                            {fmtDate(cot.fecha)}
                          </td>
                          <td style={{padding:"12px 16px"}}>
                            <span style={{padding:"3px 10px", borderRadius:12, fontSize:11,
                              fontWeight:600, color:est.color, background:est.bg}}>
                              {cot.estado}
                            </span>
                          </td>
                          <td style={{padding:"12px 16px", fontWeight:700, color:C.text}}>
                            {fmtCLP(tot)}
                          </td>
                          <td style={{padding:"12px 16px"}}>
                            {cot.token && (
                              <button
                                onClick={() => window.open(`/?token=${cot.token}`, "_blank")}
                                style={{display:"inline-flex", alignItems:"center", gap:5,
                                  padding:"5px 12px", borderRadius:7,
                                  background:C.accentL, color:C.accent,
                                  fontSize:12, fontWeight:600,
                                  border:`1px solid ${C.accent}30`,
                                  cursor:"pointer", fontFamily:"'DM Sans',sans-serif"}}>
                                <ExternalLink size={11}/> Ver propuesta
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
        )}

        {/* ── Tab Facturas ── */}
        {tab === "facturas" && (
          <div>
            {ingresos.length === 0
              ? <EmptyCard msg="No hay facturas disponibles."/>
              : (
                <div style={{background:C.white, border:`1px solid ${C.border}`,
                  borderRadius:12, overflow:"hidden",
                  boxShadow:"0 1px 3px #0000000a", marginBottom:12}}>
                  <table style={{width:"100%", borderCollapse:"collapse", fontSize:13}}>
                    <thead>
                      <tr style={{background:C.bg}}>
                        {["Folio","Fecha","Concepto","Total","Estado","PDF"].map(h => (
                          <th key={h} style={{padding:"11px 16px", textAlign:"left",
                            fontWeight:600, color:C.sub, fontSize:11,
                            letterSpacing:.5, textTransform:"uppercase",
                            borderBottom:`1px solid ${C.border}`}}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ingresos.map(ing => {
                        const est = ING_EST[ing.estado] || ING_EST.emitida;
                        const pdfHref = ing.pdf_url
                          ? ing.pdf_url
                          : ing.pdf_drive_id
                            ? `https://drive.google.com/file/d/${ing.pdf_drive_id}/view`
                            : null;
                        return (
                          <tr key={ing.id}
                            style={{borderBottom:`1px solid ${C.border}`}}
                            onMouseEnter={e => e.currentTarget.style.background = C.bg}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            <td style={{padding:"12px 16px", fontWeight:600, color:C.text}}>
                              {ing.folio || "—"}
                            </td>
                            <td style={{padding:"12px 16px", color:C.sub}}>
                              {fmtDate(ing.fecha_emision)}
                            </td>
                            <td style={{padding:"12px 16px", color:C.text, maxWidth:200}}>
                              <div style={{overflow:"hidden", textOverflow:"ellipsis",
                                whiteSpace:"nowrap"}}>
                                {ing.concepto || "—"}
                              </div>
                            </td>
                            <td style={{padding:"12px 16px", fontWeight:700, color:C.text}}>
                              {fmtCLP(ing.monto_total)}
                            </td>
                            <td style={{padding:"12px 16px"}}>
                              <span style={{padding:"3px 10px", borderRadius:12, fontSize:11,
                                fontWeight:600, color:est.color, background:est.bg}}>
                                {est.label}
                              </span>
                            </td>
                            <td style={{padding:"12px 16px"}}>
                              {pdfHref ? (
                                <a href={pdfHref} target="_blank" rel="noreferrer"
                                  style={{display:"inline-flex", alignItems:"center", gap:5,
                                    padding:"5px 12px", borderRadius:7,
                                    background:"#f1f5f9", color:C.sub,
                                    fontSize:12, fontWeight:600, textDecoration:"none",
                                    border:`1px solid ${C.border}`}}>
                                  <FileText size={11}/> Ver PDF
                                </a>
                              ) : (
                                <span style={{fontSize:12, color:C.light}}>—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            }

            {/* Totales */}
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10}}>
              <div style={{background:C.warnL, border:`1px solid ${C.warn}30`,
                borderRadius:10, padding:"14px 18px"}}>
                <div style={{fontSize:11, fontWeight:600, color:C.warn,
                  textTransform:"uppercase", letterSpacing:.5, marginBottom:4}}>
                  Pendiente de pago
                </div>
                <div style={{fontWeight:800, fontSize:20, color:C.warn}}>
                  {fmtCLP(pendiente)}
                </div>
              </div>
              <div style={{background:C.okL, border:`1px solid ${C.ok}30`,
                borderRadius:10, padding:"14px 18px"}}>
                <div style={{fontSize:11, fontWeight:600, color:C.ok,
                  textTransform:"uppercase", letterSpacing:.5, marginBottom:4}}>
                  Total pagado
                </div>
                <div style={{fontWeight:800, fontSize:20, color:C.ok}}>
                  {fmtCLP(pagado)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Tab Proyectos ── */}
        {tab === "proyectos" && (
          <PortalProyectos proyectos={proyectos} cargando={proyCargando} error={proyError}
            onRefrescar={cargarProyectos} proyectoInicial={proyectoAbrir}/>
        )}

        {/* Footer */}
        <div style={{textAlign:"center", marginTop:36, paddingTop:18,
          borderTop:`1px solid ${C.border}`}}>
          <div style={{display:"flex", alignItems:"center", justifyContent:"center",
            gap:6, fontSize:12, color:C.light}}>
            <Lock size={11}/> Portal seguro · tuaplicacion.cl
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Pantalla para crear/restablecer contraseña ────────────────────────────────
function DefinirContrasena({ esActivacion, onGuardar, onCancelar }) {
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [ver, setVer] = useState(false);
  const [err, setErr] = useState("");
  const [guardando, setGuardando] = useState(false);

  const problema = p1.length < 8 ? "Usa al menos 8 caracteres."
    : !/[A-Za-zÁÉÍÓÚÑáéíóúñ]/.test(p1) || !/\d/.test(p1) ? "Combina letras y números."
    : p1 !== p2 ? "Las contraseñas no coinciden." : "";

  async function enviar(e) {
    e.preventDefault();
    if (problema || guardando) return;
    setGuardando(true); setErr("");
    try { await onGuardar(p1); }
    catch (e2) {
      setErr(/weak|pwned|leaked/i.test(e2?.message || "") ? "Esa contraseña no es segura. Elige otra."
        : `No se pudo guardar la contraseña: ${e2?.message || "error desconocido"}`);
      setGuardando(false);
    }
  }

  const campo = {width:"100%", padding:"10px 12px", borderRadius:8, border:`1px solid ${C.border}`,
    fontSize:14, color:C.text, fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box"};
  return (
    <div style={{minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center",
      justifyContent:"center", padding:24, fontFamily:"'DM Sans',sans-serif"}}>
      <GlobalStyle/>
      <form onSubmit={enviar} style={{width:"100%", maxWidth:400, background:C.white,
        border:`1px solid ${C.border}`, borderRadius:14, padding:28, boxShadow:"0 4px 24px #0000000f"}}>
        <div style={{fontSize:22, fontWeight:800, marginBottom:4}}>
          <span style={{color:C.accent}}>tu</span><span style={{color:C.text}}>aplicacion</span>
        </div>
        <h1 style={{fontSize:17, margin:"10px 0 6px", color:C.text}}>
          {esActivacion ? "Crea tu contraseña" : "Define tu nueva contraseña"}
        </h1>
        <p style={{fontSize:13, color:C.sub, margin:"0 0 18px", lineHeight:1.6}}>
          {esActivacion
            ? "Con ella ingresarás al Portal de Clientes para seguir el avance de tu proyecto."
            : "Escribe una contraseña nueva para tu cuenta."}
        </p>
        <label htmlFor="pw1" style={{display:"block", fontSize:12, fontWeight:600, color:C.sub, marginBottom:6}}>Contraseña</label>
        <input id="pw1" type={ver ? "text" : "password"} autoComplete="new-password" value={p1}
          onChange={e => setP1(e.target.value)} style={{...campo, marginBottom:12}} required/>
        <label htmlFor="pw2" style={{display:"block", fontSize:12, fontWeight:600, color:C.sub, marginBottom:6}}>Repite la contraseña</label>
        <input id="pw2" type={ver ? "text" : "password"} autoComplete="new-password" value={p2}
          onChange={e => setP2(e.target.value)} style={campo} required/>
        <label style={{display:"flex", alignItems:"center", gap:6, fontSize:12, color:C.sub, margin:"10px 0"}}>
          <input type="checkbox" checked={ver} onChange={e => setVer(e.target.checked)}/> Mostrar contraseña
        </label>
        {(p1 || p2) && problema && <div style={{fontSize:12, color:C.warn, marginBottom:8}}>{problema}</div>}
        {err && <div role="alert" style={{fontSize:12, color:C.err, marginBottom:8}}>{err}</div>}
        <button type="submit" disabled={!!problema || guardando}
          style={{width:"100%", padding:11, borderRadius:8, border:"none", fontWeight:700, fontSize:14,
            background: problema || guardando ? C.light : C.accent, color:"#fff",
            cursor: problema || guardando ? "not-allowed" : "pointer", fontFamily:"'DM Sans',sans-serif"}}>
          {guardando ? "Guardando…" : "Guardar y entrar"}
        </button>
        <button type="button" onClick={onCancelar}
          style={{width:"100%", marginTop:10, background:"none", border:"none", color:C.sub,
            fontSize:12, cursor:"pointer", fontFamily:"'DM Sans',sans-serif"}}>
          Cancelar y cerrar sesión
        </button>
      </form>
    </div>
  );
}

// ── Helpers de render ─────────────────────────────────────────────────────────
function GlobalStyle() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
      *{box-sizing:border-box;margin:0;padding:0}
      @keyframes fadeUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
      @keyframes spin{to{transform:rotate(360deg)}}
      .spinning{animation:spin 1s linear infinite}
      input:focus,textarea:focus{outline:none;border-color:#29abe2!important;box-shadow:0 0 0 3px #29abe215}
      button:focus-visible,a:focus-visible{outline:2px solid #29abe2;outline-offset:2px}
      .portal-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:14px}
      .portal-meta{display:grid;grid-template-columns:repeat(auto-fit,minmax(120px,1fr));gap:12px}
      .portal-header{flex-wrap:wrap;gap:8px}
      @media (max-width:640px){
        .portal-header{padding:10px 16px!important;height:auto!important}
        .portal-bienvenida{order:3;width:100%}
        .portal-tabs button{padding:8px 6px!important;font-size:12px!important}
        .portal-main{padding:18px 12px!important}
      }
    `}</style>
  );
}

function EmptyCard({ msg }) {
  return (
    <div style={{background:C.white, border:`1px solid ${C.border}`,
      borderRadius:12, padding:"36px 24px", textAlign:"center",
      boxShadow:"0 1px 3px #0000000a"}}>
      <div style={{fontSize:13, color:C.sub}}>{msg}</div>
    </div>
  );
}
